import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface PolicySection {
  title: string;
  content: string | React.ReactNode;
}

export const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [openSection, setOpenSection] = useState<number | null>(null);
  const [currentLang, setCurrentLang] = useState(i18n.language);

  // Update language when URL parameter changes
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang) {
      i18n.changeLanguage(lang).then(() => {
        setCurrentLang(lang);
        setOpenSection(null);
      }).catch(error => {
        console.error('Error changing language:', error);
      });
    }
  }, [searchParams, i18n]);

  // Force re-render when language changes
  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const toggleSection = (index: number) => {
    setOpenSection(openSection === index ? null : index);
  };

  const policySections: PolicySection[] = [
    {
      title: t('privacyPolicy.sections.informationWeCollect.title', '1. Information We Collect'),
      content: (
        <div className="space-y-4">
          <p>{t('privacyPolicy.sections.informationWeCollect.intro', 'We may collect the following categories of information when you use our Services:')}</p>
          <div>
            <h4 className="font-medium mb-2">{t('privacyPolicy.sections.informationWeCollect.personalInfo.title', 'a. Personal Information You Provide')}</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacyPolicy.sections.informationWeCollect.personalInfo.items.name', 'Name, email address, contact details')}</li>
              <li>{t('privacyPolicy.sections.informationWeCollect.personalInfo.items.credentials', 'Account login credentials')}</li>
              <li>{t('privacyPolicy.sections.informationWeCollect.personalInfo.items.content', 'Content you submit (e.g., messages, audio, images, documents)')}</li>
              <li>{t('privacyPolicy.sections.informationWeCollect.personalInfo.items.feedback', 'Feedback or support requests')}</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">{t('privacyPolicy.sections.informationWeCollect.aiInputs.title', 'b. AI-Processed Inputs')}</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacyPolicy.sections.informationWeCollect.aiInputs.items.processing', 'Text, voice, or other content submitted to AI features may be processed to provide personalized results, suggestions, or actions.')}</li>
              <li>{t('privacyPolicy.sections.informationWeCollect.aiInputs.items.sensitive', 'These inputs may include sensitive or identifying information if voluntarily provided.')}</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">{t('privacyPolicy.sections.informationWeCollect.automaticData.title', 'c. Automatically Collected Data')}</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacyPolicy.sections.informationWeCollect.automaticData.items.device', 'Device and usage information (e.g., IP address, browser type, mobile identifiers)')}</li>
              <li>{t('privacyPolicy.sections.informationWeCollect.automaticData.items.analytics', 'Analytics data (via cookies or SDKs)')}</li>
              <li>{t('privacyPolicy.sections.informationWeCollect.automaticData.items.location', 'Location data (if enabled)')}</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: t('privacyPolicy.sections.howWeUseInfo.title', '2. How We Use Your Information'),
      content: (
        <div className="space-y-4">
          <p>{t('privacyPolicy.sections.howWeUseInfo.intro', 'We use your data to:')}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('privacyPolicy.sections.howWeUseInfo.items.provide', 'Provide, operate, and improve our Services')}</li>
            <li>{t('privacyPolicy.sections.howWeUseInfo.items.process', 'Process and respond to your inputs through AI features')}</li>
            <li>{t('privacyPolicy.sections.howWeUseInfo.items.personalize', 'Personalize content and user experience')}</li>
            <li>{t('privacyPolicy.sections.howWeUseInfo.items.support', 'Provide customer support')}</li>
            <li>{t('privacyPolicy.sections.howWeUseInfo.items.comply', 'Comply with legal obligations')}</li>
          </ul>
        </div>
      )
    },
    {
      title: t('privacyPolicy.sections.aiDisclosures.title', '3. AI-Specific Disclosures'),
      content: (
        <div className="space-y-4">
          <p>{t('privacyPolicy.sections.aiDisclosures.intro', 'Our Services include AI tools that process your input data (e.g., text, audio, documents) to generate responses or actions. These tools may:')}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('privacyPolicy.sections.aiDisclosures.items.analyze', 'Analyze and store inputs temporarily to improve performance')}</li>
            <li>{t('privacyPolicy.sections.aiDisclosures.items.providers', 'Operate using third-party AI providers, who may process your data on our behalf under strict confidentiality agreements')}</li>
          </ul>
          <p>{t('privacyPolicy.sections.aiDisclosures.consent', 'We do not use your AI inputs for training general-purpose AI models unless you provide explicit consent.')}</p>
        </div>
      )
    },
    {
      title: t('privacyPolicy.sections.consent.title', '4. Consent to Data Processing'),
      content: (
        <div className="space-y-4">
          <p>{t('privacyPolicy.sections.consent.intro', 'By using our Services, you consent to the collection and use of your data as described in this Privacy Policy.')}</p>
          <p>{t('privacyPolicy.sections.consent.features', 'If you use features involving AI-generated outputs or personal content processing, we will:')}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('privacyPolicy.sections.consent.items.prompt', 'Clearly prompt you for affirmative consent before collecting sensitive information')}</li>
            <li>{t('privacyPolicy.sections.consent.items.options', 'Provide options to opt out of data retention or advanced processing where feasible')}</li>
          </ul>
        </div>
      )
    },
    {
      title: t('privacyPolicy.sections.dataSharing.title', '5. Data Sharing and Disclosure'),
      content: (
        <div className="space-y-4">
          <p>{t('privacyPolicy.sections.dataSharing.intro', 'We may share your data:')}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('privacyPolicy.sections.dataSharing.items.providers', 'With service providers (including AI processors) under contractual obligations')}</li>
            <li>{t('privacyPolicy.sections.dataSharing.items.legal', 'As required by law or to protect legal rights')}</li>
            <li>{t('privacyPolicy.sections.dataSharing.items.transfer', 'In connection with a business transfer (e.g., merger or acquisition)')}</li>
          </ul>
          <p>{t('privacyPolicy.sections.dataSharing.noSell', 'We do not sell your personal information.')}</p>
        </div>
      )
    },
    {
      title: t('privacyPolicy.sections.dataRetention.title', '6. Data Retention and Security'),
      content: (
        <div className="space-y-4">
          <p>{t('privacyPolicy.sections.dataRetention.intro', 'We retain your data only as long as necessary to fulfill the purposes outlined here. We use industry-standard safeguards (encryption, access controls, etc.) to protect your information.')}</p>
          <p>{t('privacyPolicy.sections.dataRetention.deletion', 'You may request data deletion or export at any time by contacting us.')}</p>
        </div>
      )
    },
    {
      title: t('privacyPolicy.sections.rights.title', '7. Your Rights and Choices'),
      content: (
        <div className="space-y-4">
          <p>{t('privacyPolicy.sections.rights.intro', 'You have the right to:')}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('privacyPolicy.sections.rights.items.access', 'Access, correct, or delete your personal data')}</li>
            <li>{t('privacyPolicy.sections.rights.items.withdraw', 'Withdraw consent for processing')}</li>
            <li>{t('privacyPolicy.sections.rights.items.optOut', 'Opt out of marketing communications')}</li>
          </ul>
          <p>{t('privacyPolicy.sections.rights.contact', 'To exercise these rights, contact:')} <a href="mailto:contact@echoratings.com" className="text-brand-baseBlue hover:underline">contact@echoratings.com</a></p>
        </div>
      )
    },
    {
      title: t('privacyPolicy.sections.children.title', '8. Children\'s Privacy'),
      content: t('privacyPolicy.sections.children.content', 'Our Services are not intended for children under 13. We do not knowingly collect personal information from children.')
    },
    {
      title: t('privacyPolicy.sections.changes.title', '9. Changes to This Policy'),
      content: t('privacyPolicy.sections.changes.content', 'We may update this Privacy Policy periodically. We will notify you of significant changes via the app or email.')
    },
    {
      title: t('privacyPolicy.sections.contact.title', '10. Contact Us'),
      content: (
        <div className="space-y-2">
          <p>{t('privacyPolicy.sections.contact.intro', 'For questions or concerns about this policy, please contact:')}</p>
          <p>{t('privacyPolicy.sections.contact.email', 'Email:')} <a href="mailto:contact@echoratings.com" className="text-brand-baseBlue hover:underline">contact@echoratings.com</a></p>
          <p>{t('privacyPolicy.sections.contact.address', 'Address:')} 930 S 4th St, Ste 209 #4995, Las Vegas, Nevada 89101</p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('privacyPolicy.title', 'Privacy Policy')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('privacyPolicy.effectiveDate', 'Effective Date:')} 07/29/2025
          </p>
          <p className="mt-4 text-gray-600">
            {t('privacyPolicy.intro', 'Welcome to Echo Ratings Privacy Policy. Your privacy is important to us. This Privacy Policy explains how we collect, use, share, and protect your information when you use our services, including any AI-powered features, via our mobile app, website, or other platforms (collectively, the "Services").')}
          </p>
        </div>

        <div className="space-y-4">
          {policySections.map((section, index) => (
            <div
              key={`${index}-${currentLang}`}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <button
                className="w-full px-6 py-4 text-left focus:outline-none hover:bg-gray-50 transition-colors duration-200"
                onClick={() => toggleSection(index)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    {section.title}
                  </h3>
                  <span className="ml-6 flex-shrink-0">
                    <svg
                      className={`h-6 w-6 transform ${
                        openSection === index ? 'rotate-180' : ''
                      } transition-transform duration-200 text-brand-baseBlue`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </div>
              </button>
              {openSection === index && (
                <div className="px-6 pb-4">
                  <div className="text-gray-600">{section.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}; 